import { designVariables } from "@studio3t/design-variables";
import styled, { css } from "styled-components";
import { useAuth } from "@/auth";
import { useJobs, useServices } from "@/hooks/useApi";
import { DashboardEmptyState } from "./DashboardEmptyState";
import { DashboardHealth } from "./DashboardHealth";
import { DashboardRecentPipelines } from "./DashboardRecentPipelines";
import { DashboardServices } from "./DashboardServices";
import { dashboardMode } from "./dashboardMode";

const SPACING = designVariables.ui.semantic;

// Layout — the dashboard fills the viewport: the hero takes all remaining
// height below the greeting, so the page itself doesn't scroll. The cards
// carry min sizes, so on viewports too small for them the page grows and the
// page container's scroll takes over instead of squashing card content.
// The bottom padding keeps the hero's rounded corners off the viewport
// edge (Figma 15376-148241: the content area's spacingl bottom inset).
// min-height (not height) lets the page grow with an overflowing hero on
// small viewports, so that padding stays below the hero at scroll end.
const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 100%;
  padding-bottom: ${SPACING.spacingl}px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0;
  color: ${(props) => props.theme.semanticTextHeading};
`;

// Hero panel — the gradient sheet holding the dashboard cards. The 40/60px
// paddings and the 20px card corner come from the Figma semantic/spacingxl,
// spacingxxl and cards/cornerl tokens, which the installed design-variables
// package doesn't expose yet.
const Hero = styled.div<{ $centered?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  /* Shrinks with the viewport, but never below the cards' min sizes — past
     that the page scrolls, with the gradient still backing every card. */
  min-height: min-content;
  gap: ${SPACING.spacingl}px;
  padding: 60px 40px;
  border-radius: 0 0 20px 20px;
  background-image: linear-gradient(
    -27deg,
    rgba(246, 142, 52, 0.5) 8.5%,
    rgba(184, 209, 255, 0.5) 94.6%
  );
  /* The onboarding hero centres its single card. */
  ${(p) =>
    p.$centered &&
    css`
      align-items: center;
      justify-content: center;
    `}
`;

// The top cards absorb the leftover height; the pipelines card below keeps
// its natural (content) height. Below two card widths the row wraps into a
// single column.
const HeroRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  min-height: 0;
  gap: ${SPACING.spacingl}px;
`;

// Card shells for the hero blocks; the still-empty ones (Health, Services)
// receive their contents as separate components later.
const HeroCard = styled.div`
  display: flex;
  flex-direction: column;
  /* The 460px basis is the wrap threshold for the top row; the min height
     (header 70 + three status rows + the fleet-totals tiles + footer 70)
     keeps card content from being squashed into overlap — past it, the
     page scrolls instead. */
  flex: 1 1 460px;
  min-width: 0;
  min-height: 430px;
  background: ${(props) => props.theme.cards1Background};
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 1px 1px rgba(17, 17, 17, 0.06),
    0 2px 9px rgba(17, 17, 17, 0.07),
    0 0 1px rgba(17, 17, 17, 0.05);
`;

// In the hero column the plain HeroCard would grow; the pipelines card hugs
// its table instead.
const HeroCardHugging = styled(HeroCard)`
  flex: 0 0 auto;
`;

function Dashboard() {
  const { user } = useAuth();
  // Nothing at all — no pipelines and no services — swaps the hero's cards
  // for the first-pipeline onboarding (Figma 15376-148247); services alone
  // keep the cards, with Health and Recent pipelines saying they are empty
  // (Figma 16102-26437). Only once both lists have answered — while they
  // load, the cards' own skeletons carry the page.
  const { data: jobs } = useJobs();
  const { data: services } = useServices();
  const showOnboarding = dashboardMode(jobs, services) === "onboarding";

  return (
    <Page>
      <PageHeader>
        <PageTitle>{user ? `Hello ${user.name}!` : "Dashboard"}</PageTitle>
      </PageHeader>

      <Hero $centered={showOnboarding}>
        {showOnboarding ? (
          <DashboardEmptyState />
        ) : (
          <>
            <HeroRow>
              <HeroCard>
                <DashboardHealth />
              </HeroCard>
              <HeroCard>
                <DashboardServices />
              </HeroCard>
            </HeroRow>
            <HeroCardHugging>
              <DashboardRecentPipelines />
            </HeroCardHugging>
          </>
        )}
      </Hero>
    </Page>
  );
}

export default Dashboard;
